import type { InterfaceOverrideEntity } from '@unifyapps/defs/types/interface/interfaceOverride';
import { useFindDeployedInterfaceOverride } from '@unifyapps/network/generated/entity-rest-api/entity-rest-api';
import { useGetAuthDeployedInterfaceOverride } from '../hooks/fetchAndInvalidation/deployedEntities/data/useAuthDeployedInterfaceOverride';

// which fetch mechanism the caller's base-interface route uses, so the override rides the SAME route:
// 'auth-domain' → the domain-resolved logged-out endpoint, 'search' → the authenticated entity search,
// 'none' → skip the override entirely (the session-free by-id route has no auth override endpoint).
export type InterfaceOverrideSource = 'auth-domain' | 'search' | 'none';

interface UseFetchDeployedInterfaceOverrideArgs {
  interfaceId: string | undefined;
  isOverridden: boolean;
  source: InterfaceOverrideSource;
}

interface UseFetchDeployedInterfaceOverrideResult {
  override: InterfaceOverrideEntity | undefined;
  // true only while the enabled branch is actually fetching. The interface record is seeded into the
  // store ONCE, so the resource hook holds the app render on this until the override resolves —
  // otherwise the client-fetched (search) override arrives after mount and never applies.
  isLoading: boolean;
  // inspection only, deliberately NOT folded into the app-level error: a failed override must fail
  // open to the base record (override stays undefined → the merge returns base), never error the app.
  error: unknown;
}

// Both queries are called unconditionally (hook rules) and gated by the discriminator, so only the one
// matching the base-interface route runs. Both pin staleTime: Infinity to hydrate from the
// server-prefetched key instead of refetching (which would diverge from SSR).
export function useFetchDeployedInterfaceOverride({
  interfaceId,
  isOverridden,
  source,
}: UseFetchDeployedInterfaceOverrideArgs): UseFetchDeployedInterfaceOverrideResult {
  const isAuthDomain = isOverridden && source === 'auth-domain';
  const isSearch = isOverridden && source === 'search' && Boolean(interfaceId);

  const authQuery = useGetAuthDeployedInterfaceOverride<InterfaceOverrideEntity>({
    query: { enabled: isAuthDomain, staleTime: Infinity },
  });

  const searchQuery = useFindDeployedInterfaceOverride<InterfaceOverrideEntity>({
    query: {
      enabled: isSearch,
      staleTime: Infinity,
    },
  });

  const override = authQuery.data ?? searchQuery.data;

  // narrow each query's state to whichever branch is enabled, mirroring how the base-interface hook
  // ANDs a disabled query's isLoading away.
  const isLoading = (isAuthDomain && authQuery.isLoading) || (isSearch && searchQuery.isLoading);
  const error = isAuthDomain ? authQuery.error : searchQuery.error;

  return { override, isLoading, error };
}
